/** @jsx isolateComponent */

import React from 'react';
import { css } from '@emotion/core';

import { Colors, Typography } from '../../../theme';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';

import { FormLabelRequiredText } from './FormLabelRequiredText';

type FormLabelSizeProp = 'small' | 'medium';

type Props = {
  /**
   * The content to render within `FormLabel`. May be text or nodes containing
   * text, an `input`, etc.
   */
  children: React.ReactNode;
  /**
   * The `for` attribute to attach to the `label` node, associating it to
   * an external input by its `id`.
   */
  htmlFor?: string;
  /**
   * Indicates whether the input the `FormLabel` controls is disabled.
   */
  isDisabled?: boolean;
  /**
   * Indicates whether the input the `FormLabel` controls is errored.
   */
  isErrored?: boolean;
  /**
   * Indicates whether the input the `FormLabel` controls is required.
   */
  isRequired?: boolean;
  /**
   * Indicates whether the input the `FormLabel` controls is selected.
   * Typically used in conjunction with `BooleanInput`.
   */
  isSelected?: boolean;
  /**
   * Indicates the layout for the content of the `Label`.
   * `text-only` stretches the text out as far as it will go and
   * expects `children` to be a single text node.
   * `auto-content` doesn't stretch anything and lays the content out
   * based on the content sizing.
   * When not specified, no layout is applied, so the `children`
   * supplied may need to provide their own layout for total customization.
   */
  layout?: 'text-only' | 'auto-content';
  /**
   * Optional text to render inside the required label.
   */
  requiredText?: React.ReactNode;
  /**
   * Size of the `FormLabel`.
   *
   *  @default medium
   */
  size?: FormLabelSizeProp;
};

const defaultProps = {
  size: 'medium' as FormLabelSizeProp,
};

const Styles = labelStyles('FormLabel', {
  root: css`
    display: block;
    cursor: pointer;
    ${Typography.base};
    font-weight: 600;
    color: ${Colors.gray80};

    &[data-layout='text-only'] {
      display: grid;
      align-items: center;
      grid-gap: 10px;
      // 1fr to stretch text, auto for possible required node.
      grid-template-columns: 1fr auto;
    }

    &[data-layout='auto-content'] {
      display: grid;
      align-items: center;
      grid-gap: 10px;
      grid-auto-flow: column;
      justify-content: flex-start;
    }

    // BREAKING_TODO: Use new Typography.
    &[data-size='small'] {
      font-size: 12px;
      line-height: 20px;
    }

    // BREAKING_TODO: Use new Typography.
    &[data-size='medium'] {
      font-size: 14px;
      line-height: 24px;
    }

    &[data-state~='selected'] {
      color: ${Colors.ocean};
    }

    &[data-state~='errored'] {
      color: ${Colors.error};
    }

    &[data-state~='disabled'] {
      cursor: not-allowed;
    }

    &[data-state~='disabled'] {
      color: ${Colors.gray60};
    }
  `,
});

/**
 * `FormLabel` associates text to input fields.
 */
export const FormLabel = (_props: Props) => {
  const props = {
    ...defaultProps,
    ..._props,
  };
  return (
    <label
      css={Styles.root}
      data-layout={props.layout}
      data-size={props.size}
      data-state={
        [
          props.isSelected && 'selected',
          props.isErrored && 'errored',
          props.isDisabled && 'disabled',
        ]
          .filter(Boolean)
          .join(' ') || undefined
      }
      htmlFor={props.htmlFor}
    >
      {props.children}
      {props.isRequired ? (
        props.requiredText ? (
          <FormLabelRequiredText>{props.requiredText}</FormLabelRequiredText>
        ) : (
          <FormLabelRequiredText />
        )
      ) : null}
    </label>
  );
};
